<!-- src/apps/secret/components/dashboard/DashboardPrivacyBar.vue -->

<script setup lang="ts">
  import { useDropdown } from '@/shared/composables/useDropdown';
  import { useI18n } from 'vue-i18n';
  import { computed, nextTick, ref, watch } from 'vue';

  /**
   * "Privacy options" toolbar above the dashboard editor panel (Figma Make,
   * ago/2026). Two purely presentational controls:
   *
   *  - expiry: a listbox dropdown whose entries are the REAL
   *    `usePrivacyOptions().lifetimeOptions` passed down by the parent, so the
   *    operator's `secret_options.ttl_options` stays the only source of truth.
   *  - passphrase: a popover holding the passphrase input, with a green dot on
   *    the trigger once a value is set and a "remove" action inside.
   *
   * Both close on outside click and on Escape (useDropdown) and expose
   * aria-expanded / aria-controls so the state is announced.
   */

  export interface LifetimeOption {
    value: number;
    label: string;
  }

  const props = defineProps<{
    /** Available TTLs, already localised by usePrivacyOptions */
    options: LifetimeOption[];
    /** Currently selected TTL, in seconds */
    ttl: number | null;
    /** Current passphrase value (empty string when unset) */
    passphrase: string;
  }>();

  const emit = defineEmits<{
    (e: 'update:ttl', value: number): void;
    (e: 'update:passphrase', value: string): void;
  }>();

  const { t } = useI18n();

  const {
    isOpen: expiryOpen,
    dropdownRef: expiryRef,
    toggle: toggleExpiry,
    close: closeExpiry,
  } = useDropdown();

  const {
    isOpen: passphraseOpen,
    dropdownRef: passphraseRef,
    toggle: togglePassphrase,
    close: closePassphrase,
  } = useDropdown();

  const uid = Math.random().toString(36).substring(2, 9);
  const expiryMenuId = `dashboard-expiry-${uid}`;
  const passphraseMenuId = `dashboard-passphrase-${uid}`;
  const passphraseInputId = `dashboard-passphrase-input-${uid}`;

  const passphraseInput = ref<HTMLInputElement | null>(null);

  const hasPassphrase = computed(() => props.passphrase.length > 0);

  const currentLabel = computed(
    () =>
      props.options.find((option) => option.value === props.ttl)?.label ??
      t('web.secrets.selectDuration')
  );

  // useDropdown's toggle stops propagation, so the document listener that
  // would otherwise close the sibling popover never fires. Close it here.
  const onExpiryToggle = (event: Event) => {
    closePassphrase();
    toggleExpiry(event);
  };

  const onPassphraseToggle = (event: Event) => {
    closeExpiry();
    togglePassphrase(event);
  };

  const selectTtl = (value: number) => {
    emit('update:ttl', value);
    closeExpiry();
  };

  const clearPassphrase = () => {
    emit('update:passphrase', '');
    closePassphrase();
  };

  // Moving focus into the field is what makes the popover usable from the
  // keyboard: the trigger opens it, focus lands on the input, Escape closes.
  watch(passphraseOpen, async (open) => {
    if (!open) return;
    await nextTick();
    passphraseInput.value?.focus();
  });
</script>

<template>
  <!--
    Privacy control header (task 4 "elevate"): a full-width bar with its own
    surface + hairline that sits at the TOP of the composer card, so the two
    security controls read as a deliberate group rather than a thin, ignorable
    line. LEFT: a brand-tinted shield + the "privacy options" label. RIGHT: the
    two prominent controls (expiry + passphrase) whose on/off state is obvious.
  -->
  <div
    class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-day-line bg-day-100 px-4 py-3 dark:border-white/[0.13] dark:bg-night-200"
    data-testid="dashboard-privacy-bar">
    <div class="flex items-center gap-1.5">
      <svg
        class="size-[15px] text-brand-600 dark:text-brand-500"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
      <span
        class="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-day-ink dark:text-night-ink">
        {{ t('dashboard.privacy_options') }}
      </span>
    </div>

    <span
      class="hidden h-4 w-px bg-day-line-2 dark:bg-white/[0.13] sm:inline-block"
      aria-hidden="true"></span>

    <div class="flex flex-wrap items-center gap-2">
    <!-- ── expiry ── -->
    <div
      ref="expiryRef"
      class="relative">
      <button
        type="button"
        :aria-expanded="expiryOpen"
        :aria-controls="expiryMenuId"
        aria-haspopup="listbox"
        :aria-label="t('dashboard.expiry_aria', { duration: currentLabel })"
        class="dashboard-control flex items-center gap-[7px] border px-4 py-2 font-mono text-[11px] tracking-[0.04em] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-brand-500"
        :class="
          expiryOpen
            ? 'border-brand-600/40 bg-brand-600/[0.08] text-brand-600 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-500'
            : 'border-day-line-2 bg-day text-day-ink shadow-[0_1px_2px_rgba(13,21,55,0.06)] hover:border-brand-600/40 hover:bg-day-100 dark:border-white/[0.16] dark:bg-night-100 dark:text-night-ink dark:shadow-none dark:hover:border-brand-500/40 dark:hover:bg-night-200'
        "
        data-testid="dashboard-expiry-toggle"
        @click="onExpiryToggle">
        <!-- the mock keeps the leading glyph one step dimmer than the label -->
        <svg
          class="size-[13px]"
          :class="expiryOpen ? '' : 'text-day-ink-faint dark:text-night-ink-faint'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        {{ currentLabel }}
        <svg
          class="ml-0.5 size-3 text-day-ink-faint dark:text-night-ink-faint"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <ul
        v-if="expiryOpen"
        :id="expiryMenuId"
        role="listbox"
        class="absolute left-0 top-[calc(100%+4px)] z-50 max-h-72 min-w-[140px] overflow-y-auto border border-day-line-2 bg-day shadow-[0_8px_24px_rgba(13,21,55,0.1)] dark:border-white/[0.13] dark:bg-night-100 dark:shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
        <li
          v-for="option in props.options"
          :key="option.value"
          role="option"
          :aria-selected="option.value === props.ttl">
          <button
            type="button"
            class="dashboard-control flex w-full items-center justify-between whitespace-nowrap px-3.5 py-[9px] text-left font-mono text-[11px] tracking-[0.03em] transition-colors duration-100 focus-visible:bg-day-100 dark:focus-visible:bg-night-200"
            :class="
              option.value === props.ttl
                ? 'bg-brand-600/[0.06] text-brand-600 dark:bg-brand-500/10 dark:text-brand-500'
                : 'text-day-ink-muted hover:bg-day-100 dark:text-night-ink-muted dark:hover:bg-night-200'
            "
            @click="selectTtl(option.value)">
            {{ option.label }}
            <svg
              v-if="option.value === props.ttl"
              class="size-[11px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
        </li>
      </ul>
    </div>

    <!-- ── passphrase ── -->
    <div
      ref="passphraseRef"
      class="relative">
      <button
        type="button"
        :aria-expanded="passphraseOpen"
        :aria-controls="passphraseMenuId"
        class="dashboard-control flex items-center gap-[7px] border px-4 py-2 font-mono text-[11px] tracking-[0.04em] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-brand-500"
        :class="
          hasPassphrase || passphraseOpen
            ? 'border-brand-600/40 bg-brand-600/[0.08] text-brand-600 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-500'
            : 'border-day-line-2 bg-day text-day-ink shadow-[0_1px_2px_rgba(13,21,55,0.06)] hover:border-brand-600/40 hover:bg-day-100 dark:border-white/[0.16] dark:bg-night-100 dark:text-night-ink dark:shadow-none dark:hover:border-brand-500/40 dark:hover:bg-night-200'
        "
        data-testid="dashboard-passphrase-toggle"
        @click="onPassphraseToggle">
        <svg
          class="size-[13px]"
          :class="hasPassphrase || passphraseOpen ? '' : 'text-day-ink-faint dark:text-night-ink-faint'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path
            d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
        {{ t('web.COMMON.secret_passphrase') }}
        <span
          v-if="hasPassphrase"
          class="ml-0.5 inline-block size-[5px] shrink-0 rounded-full bg-emerald-500"
          aria-hidden="true"></span>
        <span
          v-if="hasPassphrase"
          class="sr-only"
          >{{ t('dashboard.passphrase_set') }}</span
        >
      </button>

      <div
        v-if="passphraseOpen"
        :id="passphraseMenuId"
        class="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[260px] border border-day-line-2 bg-day p-3.5 shadow-[0_8px_24px_rgba(13,21,55,0.1)] dark:border-white/[0.13] dark:bg-night-100 dark:shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
        <label
          :for="passphraseInputId"
          class="mb-2 block font-mono text-[9px] uppercase tracking-[0.1em] text-day-ink-faint dark:text-night-ink-faint">
          {{ t('web.COMMON.secret_passphrase') }}
        </label>
        <input
          :id="passphraseInputId"
          ref="passphraseInput"
          type="password"
          autocomplete="off"
          :value="props.passphrase"
          :placeholder="t('dashboard.passphrase_placeholder')"
          class="dashboard-field w-full border border-day-line-2 bg-day-100 px-3 py-2.5 font-mono text-xs text-day-ink caret-brand-600 placeholder:text-day-ink-faint focus:border-brand-600/50 dark:border-white/[0.13] dark:bg-night dark:text-night-ink dark:placeholder:text-night-ink-faint dark:focus:border-brand-500/50"
          data-testid="dashboard-passphrase-input"
          @input="emit('update:passphrase', ($event.target as HTMLInputElement).value)" />
        <button
          v-if="hasPassphrase"
          type="button"
          class="dashboard-control mt-2 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.04em] text-red-600 hover:underline focus-visible:underline dark:text-red-400"
          data-testid="dashboard-passphrase-remove"
          @click="clearPassphrase">
          <svg
            class="size-[11px]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18" />
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18" />
          </svg>
          {{ t('dashboard.passphrase_remove') }}
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
  /* See SecretComposer.vue: a leaked `:focus { outline: 2px solid currentColor }`
     from the reveal pages outranks Tailwind's utilities layer, so a plain mouse
     click on a toolbar trigger drew a hard 2px blue box the mock never had.
     Neutralise it here for fields and buttons alike; the buttons keep their
     `focus-visible:ring-*` utilities for keyboard users. */
  .dashboard-field:focus {
    outline: none;
    /* @tailwindcss/forms rings every focused input from its base layer; the
       mock only tints the border on focus. */
    box-shadow: none;
  }

  .dashboard-control:focus {
    outline: none;
  }
</style>
